import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Field, SelectField } from "@/pages/Equipes";
import {
  Plus, Trash2, Pencil, Pin, Megaphone, AlertTriangle, Info,
  Users as UsersIcon, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const CATEGORIES = [
  { value: "ALL", label: "Tous les membres" },
  { value: "PRO", label: "Senior Pro Dames" },
  { value: "AMATEUR", label: "Senior Amateur Dames" },
  { value: "U17", label: "U17" },
  { value: "U13", label: "U13" },
];

const ROLES = [
  { value: "joueur", label: "Joueuses" },
  { value: "parent", label: "Parents" },
  { value: "coach", label: "Entraîneurs" },
  { value: "rh", label: "RH" },
  { value: "tresorier", label: "Trésorier" },
  { value: "secretaire", label: "Secrétaire" },
  { value: "partenaire", label: "Partenaires" },
];

const PRIORITY = {
  info: { label: "Info", icon: Info, color: "bg-blue-100 text-blue-700 border-blue-200", bar: "bg-blue-500" },
  important: { label: "Important", icon: AlertTriangle, color: "bg-amber-100 text-amber-700 border-amber-200", bar: "bg-amber-500" },
  urgent: { label: "Urgent", icon: AlertTriangle, color: "bg-red-100 text-red-700 border-red-200", bar: "bg-red-500" },
};

const EMPTY = {
  title: "", content: "", priority: "info",
  target_categories: [], target_roles: [], pinned: false,
};

export default function Communication() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/announcements/${editing.id}`, form);
      } else {
        await api.post("/announcements", form);
      }
      toast.success("Annonce publiée");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  const togglePin = async (a) => {
    await api.patch(`/announcements/${a.id}`, { pinned: !a.pinned });
    load();
  };

  const toggleArr = (key, value) => {
    const arr = form[key] || [];
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    setForm({ ...form, [key]: next });
  };

  return (
    <div data-testid="communication-page">
      <PageHeader title="Communication" subtitle="Annonces internes et envoi groupé aux membres du club">
        <Button onClick={startCreate} data-testid="add-announcement-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle annonce
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        {items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
            <Megaphone className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
            <h3 className="font-barlow text-xl font-bold mb-1">Aucune annonce</h3>
            <p className="text-sm text-slate-500">Diffusez votre première annonce à vos joueuses, parents et staff.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((a) => {
              const pri = PRIORITY[a.priority] || PRIORITY.info;
              const PIcon = pri.icon;
              return (
                <div key={a.id} data-testid={`ann-${a.id}`}
                  className="bg-white border border-slate-200 rounded-md overflow-hidden flex">
                  <div className={`w-1 ${pri.bar}`} />
                  <div className="flex-1 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {a.pinned && <Pin className="h-3.5 w-3.5 text-emerald-600" />}
                          <h3 className="font-barlow font-bold text-xl tracking-tight">{a.title}</h3>
                          <Badge className={pri.color}>
                            <PIcon className="h-3 w-3 mr-1" /> {pri.label}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Par <b>{a.author_name}</b> • {formatDate(a.created_at)} •{" "}
                          <span className="text-emerald-600 inline-flex items-center gap-1">
                            <CheckCheck className="h-3 w-3" />
                            {(a.read_by || []).length} lecture{(a.read_by || []).length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => togglePin(a)}
                          data-testid={`pin-${a.id}`}
                          className={`h-8 w-8 p-0 ${a.pinned ? "text-emerald-700" : ""}`}>
                          <Pin className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(a)}
                          data-testid={`edit-ann-${a.id}`} className="h-8 w-8 p-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(a.id)}
                          data-testid={`delete-ann-${a.id}`}
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{a.content}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 self-center mr-1">
                        <UsersIcon className="h-3 w-3 inline mr-0.5" /> Destinataires
                      </span>
                      {(a.target_categories || []).length === 0 && (a.target_roles || []).length === 0 && (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200">Tous</Badge>
                      )}
                      {(a.target_categories || []).map((c) => (
                        <Badge key={c} className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          {CATEGORIES.find((x) => x.value === c)?.label || c}
                        </Badge>
                      ))}
                      {(a.target_roles || []).map((r) => (
                        <Badge key={r} className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                          {ROLES.find((x) => x.value === r)?.label || r}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {editing ? "Modifier l'annonce" : "Nouvelle annonce"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Titre" value={form.title}
              onChange={(v) => setForm({ ...form, title: v })} required testid="ann-title" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">
                Message
              </Label>
              <Textarea data-testid="ann-content" value={form.content} required
                onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Priorité" value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v })}
                options={[
                  { value: "info", label: "Info" },
                  { value: "important", label: "Important" },
                  { value: "urgent", label: "Urgent" },
                ]} testid="ann-priority" />
              <div className="flex items-end pb-1.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: !!v })}
                    data-testid="ann-pinned" />
                  <span className="text-sm">Épingler en haut</span>
                </label>
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                Cibler par catégorie d'équipe
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const active = form.target_categories?.includes(c.value);
                  return (
                    <button key={c.value} type="button"
                      onClick={() => toggleArr("target_categories", c.value)}
                      data-testid={`ann-cat-${c.value}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"
                      }`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                Cibler par rôle
              </div>
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => {
                  const active = form.target_roles?.includes(r.value);
                  return (
                    <button key={r.value} type="button"
                      onClick={() => toggleArr("target_roles", r.value)}
                      data-testid={`ann-role-${r.value}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                      }`}>
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
              💡 Aucun ciblage = visible par tous les utilisateurs. Les cibles s'ajoutent (catégorie OU rôle = visible).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-announcement-button"
              className="bg-emerald-600 hover:bg-emerald-700">
              {editing ? "Mettre à jour" : "Publier l'annonce"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
